import { z } from 'zod/v4';

// --- Metadata ---

export const StatisticsMetadataSchema = z.object({
  statistics_blob_name: z.string().optional(),
  compute_dataframe_time_seconds: z.number().optional(),
  instantiate_statistics_time_seconds: z.number().optional(),
  extract_statistics_time_seconds: z.number().optional(),
  total_time_seconds: z.number().optional(),
  statistics_index: z.number().nullable().optional(),
  block_id: z.string().optional(),
});

// Top-level metadata never carries a block_id; segment metadata always does.
export const SegmentMetadataSchema = StatisticsMetadataSchema.extend({
  block_id: z.string(),
});

// --- Player slot (matches backend PlayerSlot enum) ---

export const PlayerSlotSchema = z.enum([
  'player1',
  'player2',
  'player3',
  'player4',
  'NOT_PRESENT',
]);

// --- Rally ---

export const RallyDetailSchema = z.object({
  rally_id: z.string(),
  start_frame: z.number(),
  end_frame: z.number(),
  start_time: z.number(),
  end_time: z.number(),
  duration: z.number(),
  frame_count: z.number(),
  ball_states_summary: z.record(z.string(), z.unknown()),
  player_actions_summary: z.record(z.string(), z.unknown()),
});

// --- Match Summary ---

export const MatchSummarySchema = z.object({
  start_time: z.number(),
  end_time: z.number(),
  start_frame: z.number(),
  end_frame: z.number(),
  total_rally_time: z.number(),
  number_rallies: z.number(),
  total_shots: z.number(),
  rally_details: z.array(RallyDetailSchema),
  // Computed fields (serialized by Pydantic)
  total_game_time: z.number(),
  rally_time_percentage: z.number(),
  average_rally_duration: z.number(),
  average_shots_per_rally: z.number(),
  longest_rally: RallyDetailSchema.nullable().optional(),
  rally_with_most_shots: RallyDetailSchema.nullable().optional(),
});

// --- Highlight ---

export const HighlightSchema = z.object({
  start_time: z.number(),
  end_time: z.number(),
  player_id: z.number(),
  category: z.literal('gesture'),
});

// --- Court Zone ---

export const CourtZoneSchema = z.object({
  front_back: z.object({
    net: z.number(),
    transition: z.number(),
    back: z.number(),
  }),
  sides: z.object({
    left: z.number(),
    middle: z.number(),
    right: z.number(),
  }),
  zones: z.record(z.string(), z.number()),
});

// --- Heatmap ---

export const HeatmapDataSchema = z.object({
  heatmap: z.array(z.array(z.number())),
  x_edges: z.array(z.number()),
  y_edges: z.array(z.number()),
  total_points: z.number(),
  court_bounds: z.array(z.array(z.number())),
  grid_size: z.array(z.number()),
  player_id: z.number(),
  court_dimensions: z.object({
    width: z.number(),
    height: z.number(),
  }),
  mirror_negative_y: z.boolean(),
});

// --- Shots Tracking (per player) ---

const PositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});

const NullablePositionSchema = z.object({
  x: z.number().nullable(),
  y: z.number().nullable(),
});

export const ShotOutcomeDetailSchema = z.object({
  type: z.string().optional(),
  position: NullablePositionSchema.nullable().optional(),
  position_mirroed: NullablePositionSchema.nullable().optional(), // backend typo is intentional
  vertical_position: z.enum(['short', 'medium', 'long']).nullable().optional(),
  horizontal_position: z
    .enum(['left', 'middle', 'right'])
    .nullable()
    .optional(),
  frame: z.number().optional(),
  frames_after_shot: z.number().optional(),
});

export const ShotOutcomeEntrySchema = z.object({
  rally_id: z.string(),
  shot_frame: z.number(),
  player_position: PositionSchema,
  player_position_mirroed: PositionSchema, // backend typo is intentional
  shot_position: NullablePositionSchema,
  shot_position_mirroed: NullablePositionSchema, // backend typo is intentional
  outcome: ShotOutcomeDetailSchema,
  accepted: z.boolean(),
  is_winner: z.boolean(),
  is_error: z.boolean().optional(),
  // The fitted 3-D trajectory, kept only for the ball's speed (the rest is
  // stripped). Absent or null without a fit; `accepted: false` when the fit
  // was rejected.
  trajectory: z
    .object({
      accepted: z.boolean().optional(),
      speed_km_h: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
});

export const ShotPerformanceSchema = z.object({
  error_rate: z.number().nullable(),
  in_rate: z.number().nullable(),
  winner_rate: z.number().nullable(),
});

export const ShotDistributionEntrySchema = z.object({
  horizontal_positions: z.record(z.string(), z.number()),
  vertical_positions: z.record(z.string(), z.number()),
});

// Per shot-type breakdown inside one origin zone (where the shots landed).
export const ShotZoneByTypeSchema = z.object({
  shot_count: z.number(),
  shot_percentage_of_zone: z.number(),
  horizontal_positions: z.record(z.string(), z.number()),
  vertical_positions: z.record(z.string(), z.number()),
});

// Shot distribution keyed by the player's OWN zone (where they hit from,
// e.g. "back_left"). `all_shots` holds where every shot from that zone landed
// on the opponent half — horizontal (left/middle/right) and vertical
// (short/medium/long) fractions, each summing to ~1.
export const ShotPlayerZoneSchema = z.object({
  shot_count: z.number(),
  shot_percentage: z.number(),
  by_shot_type: z.record(z.string(), ShotZoneByTypeSchema),
  all_shots: ShotDistributionEntrySchema,
});

// Aggregate shot-speed measurements keyed by shot type. Raw trajectories are
// intentionally not part of this contract.
export const ShotSpeedAggregateSchema = z.object({
  n_accepted_shots: z.number(),
  n_with_speed: z.number(),
  mean_speed_km_h: z.number().nullable(),
  speed_sum_km_h: z.number(),
});

// Aggregate lob measurements keyed by lob subtype or the combined `lob` key.
// The backend owns the service-line classification and its reported rates.
export const LobAggregateSchema = z.object({
  n_accepted_shots: z.number(),
  n_with_height: z.number(),
  avg_height_m: z.number().nullable(),
  height_sum_m: z.number(),
  n_with_position: z.number(),
  avg_depth_m: z.number().nullable(),
  depth_sum_m: z.number(),
  n_above_service_line: z.number(),
  n_below_service_line: z.number(),
  above_service_line_pct: z.number().nullable(),
  below_service_line_pct: z.number().nullable(),
  n_intercepted: z.number(),
  intercepted_pct: z.number().nullable(),
  n_successful: z.number(),
  success_pct: z.number().nullable(),
  successful_min_depth_m: z.number(),
  service_line_depth_m: z.number(),
});

export const ShotsTrackingSchema = z
  .object({
    shot_outcomes: z.record(z.string(), z.array(ShotOutcomeEntrySchema)),
    shot_distribution: z.record(z.string(), ShotDistributionEntrySchema),
    // New (advanced): origin-zone → landing distribution. Optional so older
    // payloads without it still validate.
    shot_distribution_by_player_zone: z
      .record(z.string(), ShotPlayerZoneSchema)
      .optional(),
    performance: z.record(z.string(), ShotPerformanceSchema),
    speed: z
      .record(z.string(), ShotSpeedAggregateSchema.nullable())
      .nullable()
      .optional(),
    lobs: z
      .record(z.string(), LobAggregateSchema.nullable())
      .nullable()
      .optional(),
  })
  .nullable();

// --- Coaching Tips ---

export const CoachingTipMessageSchema = z.object({
  en: z.string(),
  pt: z.string().optional(),
  es: z.string().optional(),
  fr: z.string().optional(),
});

export const CoachingTipCategorySchema = z.enum([
  'dead_zone_exposure',
  'dead_zone_formation',
  'distance_covered',
  'movement_speed',
  'net_dominance',
  'net_zone_time',
  'team_synchronization',
]);

export const CoachingTipLevelSchema = z.enum(['positive', 'warning']);

export const CoachingTipSchema = z.object({
  category: CoachingTipCategorySchema,
  level: CoachingTipLevelSchema,
  message: CoachingTipMessageSchema,
});

// --- Player Statistics ---

export const PlayerStatisticsSchema = z.object({
  running_distance: z.number(),
  mean_velocity: z.number(),
  max_velocity: z.number(),
  court_zone: CourtZoneSchema,
  heatmap: HeatmapDataSchema,
  // Basic payloads carry this as null; a future one may omit it entirely.
  // `.default(null)` accepts both while keeping the output type `T | null`,
  // so consumers' optional chaining is unaffected. (Same resilience pattern as
  // the per-segment `highlights` fields below.)
  shots_tracking: ShotsTrackingSchema.default(null),
  coaching_tips: z.array(CoachingTipSchema).nullable().optional(),
});

// --- Shots Breakdown ---

export const ShotBreakdownPlayerSummarySchema = z.object({
  offensive_total: z.number(),
  offensive_total_ratio: z.number(),
  defensive_total: z.number(),
  defensive_total_ratio: z.number(),
  other_total: z.number(),
  unhandled_total: z.number(),
  total_shots: z.number(),
});

export const ShotBreakdownPlayerSchema = z.object({
  summary: ShotBreakdownPlayerSummarySchema,
  offensive_shots: z.record(z.string(), z.number()),
  offensive_shots_ratio: z.record(z.string(), z.number()),
  defensive_shots: z.record(z.string(), z.number()),
  defensive_shots_ratio: z.record(z.string(), z.number()),
  other_shots: z.record(z.string(), z.number()),
  unhandled_shots: z.record(z.string(), z.number()),
});

export const ShotBreakdownGameSchema = z.object({
  offensive_total: z.number(),
  offensive_total_ratio: z.number(),
  defensive_total: z.number(),
  defensive_total_ratio: z.number(),
});

export const ShotBreakdownTeamSchema = z.object({
  teammates: z.array(z.string()),
  offensive_total: z.number(),
  offensive_total_ratio: z.number(),
  offensive_shots: z.record(z.string(), z.number()),
  offensive_shots_ratio: z.record(z.string(), z.number()),
  defensive_total: z.number(),
  defensive_total_ratio: z.number(),
  defensive_shots: z.record(z.string(), z.number()),
  defensive_shots_ratio: z.record(z.string(), z.number()),
  shot_share: z.record(z.string(), z.record(z.string(), z.number())),
});

export const ShotBreakdownSchema = z
  .object({
    players: z
      .object({
        most_offensive_player: z.string(),
        most_defensive_player: z.string(),
      })
      .catchall(ShotBreakdownPlayerSchema),
    game: ShotBreakdownGameSchema,
    teams: z
      .object({
        most_offensive_team: z.string(),
      })
      .catchall(ShotBreakdownTeamSchema),
  })
  .nullable();

// --- Shots Tracking Performance (segment level) ---

export const OffensiveBallSpeedPlayerSchema = z.object({
  mean_speed_km_h: z.number().nullable(),
  n_with_speed: z.number(),
  speed_sum_km_h: z.number(),
  eligible: z.boolean(),
});

export const OffensiveBallSpeedSchema = z.object({
  hardest_hitter: z.string().nullable(),
  min_shots_required: z.number(),
  players: z.record(z.string(), OffensiveBallSpeedPlayerSchema.nullable()),
});

export const ShotsTrackingPerformanceSchema = z.object({
  performance: z
    .object({
      global: z.object({
        most_consistent_player: z.string(),
        most_decisive_player: z.string(),
      }),
    })
    .catchall(ShotPerformanceSchema),
  offensive_ball_speed: OffensiveBallSpeedSchema.nullable().optional(),
});

// --- Formation Analysis ---

export const NetDominanceSchema = z.object({
  net_time_ratio: z.number(),
  net_presence_ratio: z.number(),
  net_conquests_per_rally: z.number(),
  avg_net_hold_duration: z.number(),
  avg_back_to_net_duration: z.number(),
});

export const TeamSynchronizationSchema = z.object({
  avg_depth_difference: z.number(),
  depth_sync_score: z.number(),
  avg_lateral_spread: z.number(),
  lateral_coverage_ratio: z.number(),
});

export const TeamFormationDataSchema = z.object({
  both_net: z.number(),
  both_back: z.number(),
  net_and_transition: z.number(),
  back_and_transition: z.number(),
  net_and_back: z.number(),
  both_transition: z.number(),
  net_dominance: NetDominanceSchema,
  team_synchronization: TeamSynchronizationSchema,
});

export const TeamFormationEntrySchema = z.object({
  teammates: z.array(z.string()),
  formation_analysis: TeamFormationDataSchema,
});

export const FormationAnalysisSchema = z
  .record(z.string(), TeamFormationEntrySchema)
  .nullable()
  .optional();

// --- Team & Role ---

export const TeamInfoSchema = z.object({
  teammates: z.array(z.string()),
  confidence: z.number(),
});

export const RoleInfoSchema = z.object({
  primary_role: z.enum(['left', 'right']),
  confidence: z.number(),
});

// --- Segment ---

export const SegmentComputedStatisticsSchema = z.object({
  match_summary: MatchSummarySchema,
  // Advanced reports dropped the per-segment `highlights` array (it was never
  // read — Overview computes its own award highlights). Default to [] so both
  // legacy payloads (with the key) and new ones (without it) validate.
  highlights: z.array(HighlightSchema).optional().default([]),
  // Basic reports send this as null; advanced omits it when there is no shot
  // data. `.default(null)` accepts an omitted key too, keeping the output type
  // `T | null` so the transforms' null-guards stay valid. Without this an
  // omitted key would fail validation and blank the whole report.
  shots_breakdown: ShotBreakdownSchema.default(null),
  formation_analysis: FormationAnalysisSchema,
  teams: z.record(z.string(), TeamInfoSchema),
  roles: z.record(z.string(), RoleInfoSchema),
  players_statistics: z.record(z.string(), PlayerStatisticsSchema),
  shots_tracking_performance:
    ShotsTrackingPerformanceSchema.nullable().optional(),
});

/**
 * A player crop is either a bare base64 string (legacy) or a
 * `[base64, confidence]` tuple (current backend, where the second element is
 * the model's confidence in the crop). Normalize to the crop string at parse
 * time so the rest of the app only ever sees a `string`.
 */
export const PlayerCropSchema = z
  .union([z.string(), z.tuple([z.string(), z.number()])])
  .transform((crop) => (typeof crop === 'string' ? crop : crop[0]));

export const SegmentStatisticsSchema = z.object({
  metadata: SegmentMetadataSchema,
  player_crops: z.record(z.string(), PlayerCropSchema),
  statistics: SegmentComputedStatisticsSchema,
});

// --- Owner reports ---
//
// Each owner of a match (a booking player of it, or a signed-in Matchlytics
// user with a report token) corrects who is who across its blocks and says who
// they are, in their own report (b2b-api /statistics/{session}/identification).

const ReferencePlayerSchema = z.enum([
  'player1',
  'player2',
  'player3',
  'player4',
]);

/** The report's viewer: whether they own the match, and what they answered. */
export const OwnerReportStateSchema = z.object({
  is_owner: z.boolean(),
  corrected: z.boolean(),
  identified_as: ReferencePlayerSchema.nullable(),
});

/** The session's blocks to tell who is who in (all of them, short ones included). */
export const IdentificationSchema = z.object({
  corrected: z.boolean(),
  identified_as: ReferencePlayerSchema.nullable(),
  segments: z.array(
    z.object({
      index: z.number(),
      start_time: z.number(),
      end_time: z.number(),
      total_game_time: z.number(),
      player_crops: z.record(z.string(), z.string()),
      roles: z.record(z.string(), z.enum(['left', 'right'])),
    }),
  ),
});

// --- Top-level ---

export const StatisticsSchema = z.object({
  metadata: StatisticsMetadataSchema,
  match_summary: MatchSummarySchema,
  // Same resilience at the top level: newer advanced reports may omit or empty
  // this out. Never consumed directly, so default to [] rather than requiring it.
  highlights: z.array(HighlightSchema).optional().default([]),
  segments_statistics: z.array(SegmentStatisticsSchema),
  prediction_mode: z.enum(['basic', 'advanced']).optional().default('basic'),
  // { external_player_uuid: { block_id: PlayerSlot, ... }, ... }
  players_correspondance: z
    .record(z.string(), z.record(z.string(), PlayerSlotSchema))
    .optional()
    .default({}),
  players: z
    .array(z.array(z.tuple([z.string(), z.string()])))
    .optional()
    .default([]),
  // Present when the report was asked for its viewer (?player_id): an owner
  // who corrected who is who gets their own report, with only their own
  // identification in players_correspondance.
  owner_report: OwnerReportStateSchema.optional(),
});

// --- Player cross-session statistics ---
//
// Backend payload from GET /players/{player_id}/cross-statistics. Numeric
// fields are kept loose (z.number()) on purpose: aggregation produces floats
// that are sometimes 0 and sometimes null when no data contributed.

export const PerSessionEntrySchema = z.object({
  session_id: z.string(),
  played_at: z.string().nullable(),
  distance_m: z.number(),
  mean_velocity_ms: z.number(),
  shots: z.number().nullable(),
  game_time_s: z.number(),
  blocks_count: z.number(),
});

export const CrossStatsWindowSchema = z.object({
  start_date: z.string(),
  end_date: z.string(),
});

export const CrossStatsDateRangeSchema = z.object({
  first: z.string().nullable(),
  last: z.string().nullable(),
});

export const CrossStatsTotalsSchema = z.object({
  distance_m: z.number(),
  shots: z.number().nullable(),
  game_time_s: z.number(),
});

export const CrossStatsAveragesSchema = z.object({
  mean_velocity_ms: z.number(),
  max_velocity_ms: z.number(),
  distance_per_session_m: z.number(),
  shots_per_session: z.number().nullable(),
});

export const PlayerCrossStatisticsSchema = z.object({
  player_id: z.string(),
  sessions_count: z.number(),
  excluded_sessions_count: z.number(),
  heatmap_excluded_count: z.number(),
  window: CrossStatsWindowSchema,
  date_range: CrossStatsDateRangeSchema.nullable(),
  totals: CrossStatsTotalsSchema,
  averages: CrossStatsAveragesSchema,
  per_session: z.array(PerSessionEntrySchema),
  // Heatmap and court_zone payloads are kept as loose record blobs; they are
  // rendered by display components that already handle missing keys gracefully.
  heatmap_aggregate: z.record(z.string(), z.unknown()).nullable(),
  court_zone_average: z.record(z.string(), z.unknown()).nullable(),
  shots_breakdown_total: z.record(z.string(), z.unknown()).nullable(),
  shots_breakdown_contributors: z.number(),
});
